import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      const token = localStorage.getItem("neos_token");
      if (token) localStorage.removeItem("neos_token");
      setUser(false);
    } finally { setLoading(false); }
  }, []);

  // Listen for session-expired events dispatched by the API interceptor.
  // Reacting here (rather than hard-redirecting) lets the app show a clean
  // "please sign in again" state and preserves any unsaved work the user
  // has typed into a form — no more "app becomes unresponsive" surprises.
  useEffect(() => {
    const handler = () => setUser(false);
    if (typeof window !== "undefined") {
      window.addEventListener("neos:session-expired", handler);
      return () => window.removeEventListener("neos:session-expired", handler);
    }
    return undefined;
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("neos_token", data.token);
      setUser({ ...data.user, provider: "jwt" });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      if (user?.provider === "google") await api.post("/auth/google/logout");
      else await api.post("/auth/logout");
    } catch (e) {
      // Logout is best-effort — the client-side state and token are still cleared below.
      // Log for observability but do not surface to the user.
      console.warn("Logout request failed (client state cleared regardless):", e?.message || e);
    }
    localStorage.removeItem("neos_token");
    setUser(false);
  }, [user?.provider]);

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh: checkAuth }),
    [user, loading, login, logout, checkAuth]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
